import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, Sparkles, LogIn, Download, RefreshCw, CheckCircle2, XCircle, RotateCcw, Mail, FolderOpen } from "lucide-react";
import { analysisApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GenerationStatus } from "@/components/business/GenerationStatus";

const STAGES = ["Porządkuję Twoje dane", "Rozdzielam fakty od hipotez", "Analizuję klienta i problem", "Oceniam rynek, ofertę i koszty", "Buduję rekomendację i plan 30/90 dni"];
const decMeta = {
  rozwijaj: { color: "#16a34a", icon: CheckCircle2, label: "ROZWIJAJ" },
  "zmień": { color: "#ca8a04", icon: RefreshCw, label: "ZMIEŃ" },
  "odrzuć": { color: "#dc2626", icon: XCircle, label: "ODRZUĆ" },
};

export default function BizAnalysis() {
  const { entry: entryParam } = useParams();
  const entry = entryParam === "firma" ? "firma" : "idea";
  const SID_KEY = `promptivo.biz.analysisSid.${entry}`;
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlSid = searchParams.get("sid");
  const [cfg, setCfg] = useState(null);
  const [sid, setSid] = useState(null);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);
  const [locked, setLocked] = useState(false);
  const [clarify, setClarify] = useState([]);
  const [missing, setMissing] = useState([]);
  const saveTimer = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const c = await analysisApi.config(entry);
        setCfg(c);
        if (user) { try { await (await import("@/lib/api")).businessApi.claim(); } catch { /* ignore */ } }
        // 1) Jawny identyfikator sesji z URL (np. z „Moje analizy") — niezależny od localStorage/urządzenia.
        //    Własność sesji waliduje backend (_owned_analysis); obcy/niepoprawny id → błąd i powrót do zwykłego trybu.
        if (urlSid) {
          try {
            const s = await analysisApi.getSession(urlSid);
            setSid(s.id); setAnswers(s.answers || {}); setStep(s.current_step || 0);
            try { localStorage.setItem(SID_KEY, s.id); } catch { /* ignore */ }
            if (s.report) { setReport(s.report); setLocked(!!s.locked); }
            return;
          } catch { toast.error("Nie udało się otworzyć wskazanej analizy."); }
        }
        let id = null;
        try { id = localStorage.getItem(SID_KEY); } catch { /* ignore */ }
        if (id) {
          try {
            const s = await analysisApi.getSession(id);
            setSid(s.id); setAnswers(s.answers || {}); setStep(s.current_step || 0);
            if (s.report) { setReport(s.report); setLocked(!!s.locked); }
            return;
          } catch { /* stale */ }
        }
        const s = await analysisApi.createSession(entry, {});
        setSid(s.id);
        try { localStorage.setItem(SID_KEY, s.id); } catch { /* ignore */ }
      } catch { toast.error("Nie udało się wczytać Analizy. Odśwież stronę."); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, entry, urlSid]);

  const scheduleSave = useCallback((next, nextStep) => {
    if (!sid) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => { analysisApi.patchSession(sid, { answers: next, currentStep: nextStep }).catch(() => {}); }, 800);
  }, [sid]);

  const setField = (id, v) => {
    const empty = v == null || v === "" || (Array.isArray(v) && v.length === 0) || v === false;
    if (!empty) setMissing((m) => (m.includes(id) ? m.filter((x) => x !== id) : m));
    setAnswers((p) => { const n = { ...p, [id]: v }; scheduleSave(n, step); return n; });
  };
  const goStep = (n) => { setStep(n); scheduleSave(answers, n); };

  const steps = cfg?.steps || [];
  const isSummary = step === steps.length - 1;

  // Mapowanie pola → krok i etykieta (do wskazania brakujących wymaganych pól).
  const fieldMeta = (id) => {
    for (let i = 0; i < steps.length; i++) {
      const f = (steps[i].fields || []).find((x) => x.id === id);
      if (f) return { stepIndex: i, label: f.label };
    }
    return null;
  };

  const scrollToField = (id) => {
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="ana-field-${id}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        const input = el.querySelector("input, textarea");
        if (input) { try { input.focus({ preventScroll: true }); } catch { /* ignore */ } }
      }
    }, 180);
  };

  const run = async () => {
    setRunning(true); setClarify([]); setMissing([]);
    try {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      await analysisApi.patchSession(sid, { answers, currentStep: step }).catch(() => {});
      const d = await analysisApi.run(sid);
      if (d.needsClarification) { setClarify(d.questions || []); toast.message("Doprecyzuj jedną rzecz, aby analiza była trafna."); return; }
      setReport(d.report || null); setLocked(!!d.locked);
    } catch (e) {
      const msg = e?.response?.data?.detail;
      if (e?.response?.status === 422 && Array.isArray(msg?.missing) && msg.missing.length) {
        const metas = msg.missing.map((id) => ({ id, ...(fieldMeta(id) || { stepIndex: 0, label: id }) }));
        setMissing(msg.missing);
        // Przejdź do kroku z PIERWSZYM brakującym polem (po kolejności kroków).
        const first = metas.reduce((a, b) => (b.stepIndex < a.stepIndex ? b : a), metas[0]);
        setStep(first.stepIndex);
        scheduleSave(answers, first.stepIndex);
        scrollToField(first.id);
        const labels = metas.map((m) => m.label);
        const shown = labels.slice(0, 3).join(", ");
        const extra = labels.length > 3 ? ` i ${labels.length - 3} więcej` : "";
        toast.error(`Uzupełnij wymagane pola: ${shown}${extra}.`);
        return;
      }
      toast.error(typeof msg === "string" ? msg : "Nie udało się przygotować analizy. Spróbuj ponownie.");
    } finally { setRunning(false); }
  };

  const download = (fmt) => window.open(analysisApi.exportUrl(sid, fmt), "_blank");

  if (!cfg) return <div className="flex min-h-screen items-center justify-center bg-brand-bg"><Loader2 className="animate-spin text-brand-accent" /></div>;

  return (
    <div className="min-h-screen bg-brand-bg text-brand-text">
      <header className="mx-auto flex max-w-content items-center justify-between px-4 py-5 md:px-8">
        <Link to="/biznes" className="inline-flex items-center gap-2 text-sm text-brand-sec hover:text-brand-text"><ArrowLeft size={16} /> Biznes i pomysły</Link>
        <div className="flex items-center gap-4">
          {user && <Link to="/biznes/analizy" data-testid="ana-my-analyses-link" className="inline-flex items-center gap-1.5 text-sm text-brand-sec hover:text-brand-text"><FolderOpen size={15} /> Moje analizy</Link>}
          {user === false && <Link to="/logowanie" className="inline-flex items-center gap-1.5 text-sm text-brand-sec hover:text-brand-text"><LogIn size={15} /> Zaloguj się</Link>}
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 pb-28 md:px-8">
        {!report ? (
          <>
            <div className="mb-6">
              <p className="text-[13px] font-semibold text-brand-accent">Analiza · {entry === "firma" ? "Prowadzę firmę" : "Mam pomysł"} · Krok {step + 1} z {steps.length}</p>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-brand-elevated"><div className="h-full rounded-full bg-brand-accent transition-all" style={{ width: `${((step + 1) / steps.length) * 100}%` }} /></div>
            </div>
            <h1 className="font-display text-2xl font-semibold sm:text-3xl" data-testid="ana-step-title">{steps[step].title}</h1>
            {steps[step].desc && <p className="mt-2 text-brand-sec">{steps[step].desc}</p>}

            {clarify.length > 0 && (
              <div className="mt-4 rounded-panel border border-amber-500/40 bg-amber-500/10 p-4 text-[13px] text-amber-200" data-testid="ana-clarify">
                <p className="font-semibold">Doprecyzuj:</p><ul className="mt-1 list-disc pl-5">{clarify.map((q, i) => <li key={i}>{q}</li>)}</ul>
              </div>
            )}

            {!isSummary ? (
              <div className="mt-6 space-y-6" data-testid="ana-step-fields">
                {steps[step].fields.map((f) => <Field key={f.id} f={f} value={answers[f.id]} onChange={(v) => setField(f.id, v)} invalid={missing.includes(f.id)} />)}
              </div>
            ) : (
              <div className="mt-6" data-testid="ana-summary">
                <div className="space-y-6">
                  {steps[step].fields.map((f) => <Field key={f.id} f={f} value={answers[f.id]} onChange={(v) => setField(f.id, v)} invalid={missing.includes(f.id)} />)}
                </div>
                <div className="mt-6 rounded-panel border border-brand-line bg-brand-surface p-5">
                  <p className="text-sm text-brand-sec">To ostatni krok. Uruchom analizę — możesz wrócić i poprawić dowolny krok.</p>
                  <button type="button" data-testid="ana-run" disabled={running} onClick={run} className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-button bg-brand-accent px-6 py-3 text-sm font-semibold text-brand-on transition-transform hover:-translate-y-0.5 disabled:opacity-60">
                    {running ? <Loader2 size={17} className="animate-spin" /> : <Sparkles size={16} />} {running ? "Analizuję…" : "Uruchom analizę"}
                  </button>
                  <GenerationStatus stages={STAGES} active={running} className="mt-4" />
                  {cfg.warning && <p className="mt-2 text-center text-[12px] text-brand-muted">{cfg.warning}</p>}
                </div>
              </div>
            )}

            <div className="mt-8 flex items-center justify-between">
              <button type="button" data-testid="ana-back" disabled={step === 0} onClick={() => goStep(step - 1)} className="inline-flex items-center gap-2 rounded-button border border-brand-line px-4 py-2.5 text-sm font-semibold disabled:opacity-40"><ArrowLeft size={15} /> Wstecz</button>
              {!isSummary && <button type="button" data-testid="ana-next" onClick={() => goStep(step + 1)} className="inline-flex items-center gap-2 rounded-button bg-brand-accent px-5 py-2.5 text-sm font-semibold text-brand-on">Dalej <ArrowRight size={15} /></button>}
            </div>
          </>
        ) : (
          <Report report={report} locked={locked} labels={cfg.scoreLabels} onBack={() => setReport(null)} onDownload={download} onRerun={run} running={running} loggedIn={!!user} sid={sid} userEmail={user?.email || ""} />
        )}
      </main>
    </div>
  );
}

function Field({ f, value, onChange, invalid }) {
  const base = `w-full rounded-field border bg-brand-bg px-4 py-2.5 text-[15px] text-brand-text outline-none focus:border-brand-accent ${invalid ? "border-red-500/70" : "border-brand-line"}`;
  return (
    <div data-testid={`ana-field-${f.id}`} className={invalid ? "scroll-mt-24 rounded-field ring-1 ring-red-500/40" : "scroll-mt-24"}>
      <label className="mb-2 block text-sm font-medium text-brand-text">{f.label}{f.required && <span className="text-brand-accent"> *</span>}</label>
      {f.type === "single" && (
        <div className={`flex flex-wrap gap-2 ${invalid ? "rounded-field p-0.5" : ""}`}>
          {f.options.map((o) => (
            <button key={o} type="button" data-testid={`ana-opt-${f.id}-${o}`} onClick={() => onChange(value === o ? "" : o)} className={`rounded-full border px-3.5 py-2 text-[13px] font-medium transition-colors ${value === o ? "border-brand-accent bg-brand-accent/10 text-brand-text" : invalid ? "border-red-500/60 text-brand-sec hover:text-brand-text" : "border-brand-line text-brand-sec hover:text-brand-text"}`}>{o}</button>
          ))}
        </div>
      )}
      {f.type === "text" && <input className={base} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={f.placeholder || ""} />}
      {f.type === "textarea" && <textarea rows={3} className={base} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={f.placeholder || ""} />}
      {invalid && <p className="mt-1.5 text-[12px] font-medium text-red-400" data-testid={`ana-field-error-${f.id}`}>To pole jest wymagane.</p>}
    </div>
  );
}

function Report({ report, locked, labels, onBack, onDownload, onRerun, running, loggedIn, sid, userEmail }) {
  const rec = report.recommendation || {};
  const meta = decMeta[rec.decision] || { color: "#6b7280", icon: Sparkles, label: (rec.decision || "").toUpperCase() };
  const Icon = meta.icon;
  const [emailOpen, setEmailOpen] = useState(false);
  return (
    <div data-testid="ana-report">
      <button type="button" onClick={onBack} className="mb-4 inline-flex items-center gap-2 text-sm text-brand-sec hover:text-brand-text"><ArrowLeft size={15} /> Wróć do wywiadu</button>

      <div className="rounded-panel border p-5" style={{ borderColor: meta.color + "66", background: meta.color + "14" }} data-testid="ana-recommendation">
        <div className="flex items-center gap-3">
          <Icon size={26} style={{ color: meta.color }} />
          <div>
            <p className="font-display text-xl font-bold" style={{ color: meta.color }}>{meta.label}</p>
            <p className="text-[13px] text-brand-sec">Rekomendacja · pewność: {rec.confidence || "—"} · wynik {report.score ?? "—"}/100</p>
          </div>
        </div>
        {rec.rationale && <p className="mt-3 text-[14px] text-brand-text">{rec.rationale}</p>}
      </div>

      {report.summary && <p className="mt-4 text-brand-sec">{report.summary}</p>}

      {locked ? (
        <div className="mt-5 rounded-panel border border-brand-accent/40 bg-brand-accent/10 p-4" data-testid="ana-gate">
          <p className="text-sm text-brand-text">To skrócony podgląd. Załóż bezpłatne konto, aby zobaczyć pełny raport (klient, problem, rynek, konkurencja, ryzyka, fakty vs hipotezy, plan 30/90 dni) i zapisać go na stałe.</p>
          <Link to={`/rejestracja?returnTo=${encodeURIComponent(window.location.pathname)}`} className="mt-3 inline-flex items-center gap-2 rounded-button bg-brand-accent px-5 py-2.5 text-sm font-semibold text-brand-on" data-testid="ana-gate-register"><Sparkles size={16} /> Załóż konto i odblokuj pełny raport</Link>
        </div>
      ) : (
        <>
          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" data-testid="ana-export-pdf" onClick={() => onDownload("pdf")} className="inline-flex items-center gap-2 rounded-button border border-brand-line px-4 py-2 text-[13px] font-semibold hover:border-brand-accent/60"><Download size={15} /> PDF</button>
            <button type="button" data-testid="ana-export-docx" onClick={() => onDownload("docx")} className="inline-flex items-center gap-2 rounded-button border border-brand-line px-4 py-2 text-[13px] font-semibold hover:border-brand-accent/60"><Download size={15} /> DOCX</button>
            <button type="button" data-testid="ana-rerun" disabled={running} onClick={onRerun} className="inline-flex items-center gap-2 rounded-button border border-brand-line px-4 py-2 text-[13px] font-semibold hover:border-brand-accent/60 disabled:opacity-50"><RotateCcw size={15} /> Przelicz ponownie</button>
            <button type="button" data-testid="ana-email-open" onClick={() => setEmailOpen(true)} className="inline-flex items-center gap-2 rounded-button border border-brand-line px-4 py-2 text-[13px] font-semibold hover:border-brand-accent/60"><Mail size={15} /> Wyślij na e-mail</button>
          </div>
          {emailOpen && <EmailReportModal sid={sid} versionId={report.version_id} defaultEmail={userEmail} onClose={() => setEmailOpen(false)} />}

          <div className="mt-6 space-y-4">
            <Sec title="Klient" v={report.client} /><Sec title="Problem" v={report.problem} />
            <Sec title="Rynek" v={report.market} />
            <ListSec title="Konkurencja i alternatywy" items={report.competition} render={(c) => <><strong>{c.kto}</strong> — {c.jakSieWyrozniasz}</>} />
            <Sec title="Oferta" v={report.offer} /><Sec title="Model przychodów" v={report.revenueModel} />
            <Sec title="Ceny" v={report.pricing} /><Sec title="Koszty" v={report.costs} />
            <ListSec title="Ryzyka" items={report.risks} tone="risk" render={(r) => <><span className="font-semibold">[{r.waga}]</span> {r.ryzyko} → <span className="text-brand-sec">{r.mitygacja}</span></>} />
            <ListSec title="Braki danych" items={report.dataGaps} />
            <ListSec title="Fakty (z Twoich danych)" items={report.facts} tone="fact" />
            <ListSec title="Hipotezy do sprawdzenia" items={report.assumptions} tone="hyp" />
            <ListSec title="Co musiałoby się zmienić, aby decyzja była inna" items={report.decisionChangeCondition} tone="risk" />
            <ListSec title="Plan walidacji" items={report.validationPlan} />
            <ListSec title="Plan na 30 dni" items={report.plan30} />
            <ListSec title="Plan na 90 dni" items={report.plan90} />
          </div>
          {report.scoreBreakdown && <p className="mt-4 text-[12px] text-brand-muted">{Object.entries(report.scoreBreakdown).map(([k, v]) => `${labels?.[k] || k}: ${v}/10`).join(" · ")}. {report.scoreRationale}</p>}
          {!loggedIn && <p className="mt-2 text-[12px] text-brand-muted">Zaloguj się, aby zapisać ten raport na stałe.</p>}
        </>
      )}
    </div>
  );
}

const Sec = ({ title, v }) => v ? (<div className="rounded-panel border border-brand-line bg-brand-surface p-4"><p className="mb-1 font-display text-base font-semibold">{title}</p><p className="text-[14px] text-brand-sec">{v}</p></div>) : null;
function ListSec({ title, items, render, tone }) {
  if (!items || !items.length) return null;
  const dot = tone === "fact" ? "text-emerald-500" : tone === "hyp" ? "text-amber-500" : tone === "risk" ? "text-red-500" : "text-brand-accent";
  return (
    <div className="rounded-panel border border-brand-line bg-brand-surface p-4">
      <p className="mb-2 font-display text-base font-semibold">{title}</p>
      <ul className="space-y-1.5 text-[14px] text-brand-sec">
        {items.map((it, i) => <li key={i} className="flex gap-2"><span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-current ${dot}`} /><span>{render ? render(it) : it}</span></li>)}
      </ul>
    </div>
  );
}

const MARKETING_CLAUSE = "Chcę otrzymywać e-mailem informacje o nowych narzędziach, materiałach i ofertach Promptivo. Zgodę mogę wycofać w każdej chwili.";
const fmtDate = (v) => { try { return new Date(v).toLocaleString("pl-PL", { dateStyle: "medium", timeStyle: "short" }); } catch { return v; } };
const statusLabel = { sent: "Wysłano", mock: "Wysłano (tryb testowy)", failed: "Błąd wysyłki", mock_blocked: "Nieskonfigurowano" };
const deliveryLabel = { delivered: "dostarczono", bounced: "odrzucono", complained: "oznaczono jako spam", delayed: "opóźniono", failed: "błąd dostawy", queued: "w kolejce" };

function EmailReportModal({ sid, versionId, defaultEmail, onClose }) {
  const [recipient, setRecipient] = useState(defaultEmail || "");
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(() => {
    analysisApi.emailHistory(sid).then((d) => setHistory(d.items || [])).catch(() => {});
  }, [sid]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const send = async () => {
    setError("");
    const email = recipient.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { setError("Podaj poprawny adres e-mail odbiorcy."); return; }
    setSending(true);
    try {
      await analysisApi.emailReport(sid, { recipient: email, versionId, marketingConsent: consent });
      toast.success("Raport został wysłany. Możesz wrócić do projektu albo przejść do kolejnego narzędzia Promptivo.");
      loadHistory();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      if (e?.response?.status === 429) {
        const ra = Number(e?.response?.headers?.["retry-after"]);
        const when = ra ? (ra >= 60 ? `za ${Math.ceil(ra / 60)} min` : `za ${ra} s`) : "za chwilę";
        setError(`${typeof msg === "string" ? msg : "Osiągnięto limit wysyłek raportu."} Spróbuj ponownie ${when}.`);
      } else {
        setError(typeof msg === "string" ? msg : "Nie udało się wysłać raportu. Spróbuj ponownie za chwilę.");
      }
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="ana-email-modal" onClick={onClose}>
      <div className="w-full max-w-md rounded-panel border border-brand-line bg-brand-surface p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <p className="font-display text-lg font-semibold">Wyślij raport na e-mail</p>
          <button type="button" data-testid="ana-email-close" onClick={onClose} className="text-brand-sec hover:text-brand-text"><XCircle size={20} /></button>
        </div>
        <p className="mt-1 text-[13px] text-brand-sec">Wyślemy PDF tej wersji raportu. Sprawdź adres przed wysłaniem — możesz wpisać inny.</p>

        <label className="mt-4 block text-sm font-medium">Adres e-mail odbiorcy</label>
        <input data-testid="ana-email-recipient" type="email" value={recipient} onChange={(e) => setRecipient(e.target.value)} className="mt-1 w-full rounded-field border border-brand-line bg-brand-bg px-4 py-2.5 text-[15px] outline-none focus:border-brand-accent" placeholder="np. jan@firma.pl" />

        <label className="mt-4 flex cursor-pointer items-start gap-2.5 text-[13px] text-brand-sec">
          <input data-testid="ana-email-consent" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 accent-brand-accent" />
          <span>{MARKETING_CLAUSE}</span>
        </label>
        <p className="mt-1 text-[11px] text-brand-muted">Zaznaczenie zgody jest opcjonalne i nie jest warunkiem wysłania raportu.</p>

        {error && <p data-testid="ana-email-error" className="mt-3 rounded-field border border-red-500/40 bg-red-500/10 p-2.5 text-[13px] text-red-300">{error}</p>}

        <button type="button" data-testid="ana-email-send" disabled={sending} onClick={send} className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-button bg-brand-accent px-5 py-3 text-sm font-semibold text-brand-on transition-transform hover:-translate-y-0.5 disabled:opacity-60">
          {sending ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />} {sending ? "Wysyłam…" : error ? "Ponów wysyłkę" : "Wyślij raport"}
        </button>

        {history.length > 0 && (
          <div className="mt-5 border-t border-brand-line pt-4" data-testid="ana-email-history">
            <p className="mb-2 text-[13px] font-semibold text-brand-text">Historia wysyłek</p>
            <ul className="space-y-1.5 text-[12px] text-brand-sec">
              {history.map((h) => (
                <li key={h.id} className="flex items-center justify-between gap-2">
                  <span className="truncate">{h.recipient}</span>
                  <span className="shrink-0 text-brand-muted">{fmtDate(h.created_at)} · <span className={h.status === "failed" || h.status === "mock_blocked" ? "text-red-400" : "text-emerald-400"}>{statusLabel[h.status] || h.status}</span>{h.deliveryStatus && deliveryLabel[h.deliveryStatus] ? <span className="text-brand-muted"> · {deliveryLabel[h.deliveryStatus]}</span> : null}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

